/**
 * Story 020 AC14 — cross-tenant AI-finding visibility guard.
 *
 * Placeholder for Story 067 (`prohibit-ai-result-sharing-flag`,
 * cross-cutting). Story 067 wires up the project-level
 * `prohibitAiResultSharing` flag + the per-project owning-tenant check;
 * Story 020 pre-wires the consumer surface so when 067 lands the hook
 * flips to real data without touching `<CataloguePopover/>`.
 *
 * Today: every selection is allowed (no project sets the flag yet).
 *
 * FOLLOWUP(story-067): replace the placeholder body with:
 *   1. `useDataAcquisitionDetailQuery` → resolve project + owningTenant.
 *   2. `useProjectQuery(projectId)` → read `prohibitAiResultSharing`.
 *   3. `useMe()` → compare `me.tenantId` against `project.ownerTenantId`.
 *   4. Cross-reference the selection's tile origin (`source==='aiFinding'`)
 *      against the resolved guard.
 *
 * Sources:
 *   - docs/backend-requirements/072-prohibit-ai-result-sharing-flag.md
 *   - docs/stories/cross-cutting/067-prohibit-ai-result-sharing-flag.md
 *   - .vibe/62 §6 D-2
 */

export interface AiSharingGuard {
  /** True when every id in `ids` may be reclassified. */
  allowedForSelection: (ids: readonly string[]) => boolean;
  /**
   * Reason for the disabled / no-op state; `null` when allowed. Used by
   * the Reclassify menu item tooltip + the partial-apply Snackbar copy.
   */
  reasonForSelection: (ids: readonly string[]) => string | null;
  /**
   * Subset of `ids` that pass the guard. Used by AC14's "partial apply
   * with snackbar" path: when the selection mixes existing defects with
   * AI-finding tiles, the popover applies to the existing-defect subset
   * and surfaces a "{N} AI-finding tile(s) skipped — not shared with
   * your tenant." Snackbar.
   */
  filterAllowed: (ids: readonly string[]) => readonly string[];
}

/**
 * Returns the guard for the active project. FOLLOWUP(story-067): wire to
 * `useProjectQuery` once the contract bump on `companies/v1` lands.
 */
export function useAiSharingGuard(): AiSharingGuard {
  // FOLLOWUP(story-067): replace with project-flag + tenant-id resolution.
  return {
    allowedForSelection: () => true,
    reasonForSelection: () => null,
    filterAllowed: (ids) => ids,
  };
}
